import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostListener,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    QueryList,
    Renderer2,
    ViewChildren
} from '@angular/core';
import {createPopper, Instance as PopperInstance, Modifier} from '@popperjs/core';
import {OffsetModifier} from '@popperjs/core/lib/modifiers/offset';
import * as _ from 'lodash-es';
import {combineLatest, Subject, timer} from 'rxjs';
import {map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {fromEventOutsideAngularZone} from '../../common/rxjs/from-event-outside-angular-zone';
import {destroy$} from '../destroyable';
import {ModalService} from '../modal';
import {OverlayService} from '../overlay';
import {createPopperDropdownOffsetModifier} from '../popper-utils/modifiers/popper-dropdown-offset';
import {PopperOptions} from '../popper-utils/popper-options';
import {DropdownMenuItem} from './dropdown-menu-item';

const FIRST_ITEM_INDEX = 0;
const ITEM_NOT_SELECTED_INDEX = -1;
const ITEM_HOVER_CLASS = '-hover';

@Component({
    selector: 'ul[dropdown-menu]',
    templateUrl: './dropdown-menu.component.html',
    styleUrls: ['./dropdown-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DropdownMenuComponent<ItemValue = any> implements OnInit, AfterViewInit, OnDestroy {

    @Input()
    public targetElement: ElementRef;

    @Input()
    public menuItems: Array<DropdownMenuItem<ItemValue>>;

    @Input()
    public onCloseCallback: (value?: ItemValue, item?: DropdownMenuItem<ItemValue>) => void;

    @ViewChildren('item', {read: ElementRef})
    public itemQueryList: QueryList<ElementRef>;

    public preselectedMenuItemIndex: number = ITEM_NOT_SELECTED_INDEX;

    public readonly destroy$ = destroy$();

    public readonly changeDetectionSafeItems$ = new Subject<QueryList<ElementRef>>();
    public readonly changeDetectionSafeItemsUpdate$ = this.changeDetectionSafeItems$.pipe(
        switchMap(list => list.changes)
    );

    protected popper: PopperInstance;

    constructor(
        protected elementRef: ElementRef,
        protected modalService: ModalService,
        protected clickOutsideService: OverlayService,
        protected renderer: Renderer2,
        protected ngZone: NgZone
    ) {}

    public ngOnInit(): void {
        this.createOverlay();
    }

    public ngAfterViewInit(): void {
        this.initPopper();
        this.listenToItemsHover();
        this.listenToListItems();
    }

    public ngOnDestroy(): void {
        this.destroyPopper();
    }

    public close(menuItem?: DropdownMenuItem): void {
        if (menuItem && menuItem.callback) {
            menuItem.callback();
        }

        const value: ItemValue = menuItem && menuItem.value;

        if (this.onCloseCallback) {
            this.onCloseCallback(value, menuItem);
        }

        this.modalService.close(this);
    }

    public onMenuItemClick(menuItem?: DropdownMenuItem): void {
        if (menuItem && !menuItem.isSeparator) {
            this.close(menuItem);
        }
    }

    /*
     * Keyboard and mouse events handling
     */

    public getPreviousItemIndex(currentItemIndex: number): number {
        const LAST_ITEM_INDEX = this.menuItems.length - 1;

        return (currentItemIndex === FIRST_ITEM_INDEX)
            ? LAST_ITEM_INDEX
            : _.findLastIndex(
                this.menuItems,
                menuItem => !menuItem.isSeparator,
                currentItemIndex - 1
            );
    }

    public getNextItemIndex(currentItemIndex: number): number {
        const LAST_ITEM_INDEX = this.menuItems.length - 1;

        return (currentItemIndex === LAST_ITEM_INDEX)
            ? FIRST_ITEM_INDEX
            : _.findIndex(
                this.menuItems,
                menuItem => !menuItem.isSeparator,
                currentItemIndex + 1
            );
    }

    public selectPreselectedMenuItem(): void {
        this.close(this.menuItems[this.preselectedMenuItemIndex]);
    }

    @HostListener('document:keydown', ['$event'])
    public onKeyDown(event: KeyboardEvent): void {
        const KEY_UP     = 38;
        const KEY_DOWN   = 40;
        const KEY_ENTER  = 13;
        const KEY_SPACE  = 32;
        const KEY_ESCAPE = 27;

        switch (event.keyCode) {
            case KEY_UP:
                event.preventDefault();
                this.setPreselectedMenuItemIndex(
                    this.getPreviousItemIndex(this.preselectedMenuItemIndex)
                );

                break;
            case KEY_DOWN:
                event.preventDefault();
                this.setPreselectedMenuItemIndex(
                    this.getNextItemIndex(this.preselectedMenuItemIndex)
                );

                break;
            case KEY_ENTER:
            case KEY_SPACE:
                event.preventDefault();
                this.selectPreselectedMenuItem();

                break;
            case KEY_ESCAPE:
                event.preventDefault();
                this.close();

                break;
        }
    }

    protected setPreselectedMenuItemIndex(index: number): void {
        this.preselectedMenuItemIndex = index;
    }

    protected initPopper(popperOptions: PopperOptions = {}): void {
        this.popper = createPopper(
            this.targetElement.nativeElement,
            this.elementRef.nativeElement,
            {
                placement: 'bottom-end',
                modifiers: this.getPopperModifiers(),
                ...popperOptions
            }
        );

        this.popper.update();
    }

    protected getPopperModifiers(): Array<Modifier<any, any>> {
        return [
            this.getPopperOffsetModifier()
        ];
    }

    protected getPopperOffsetModifier(): OffsetModifier {
        return createPopperDropdownOffsetModifier();
    }

    protected destroyPopper(): void {
        if (this.popper) {
            this.popper.destroy();
        }
    }

    protected createOverlay(): void {
        this.clickOutsideService.create({
            target: this.modalService.getByInstance(this)
        }).subscribe(() => this.close());
    }

    protected listenToItemsHover(): void {
        const itemsElements$ = this.itemQueryList.changes.pipe(
            startWith(this.itemQueryList),
            map(() => this.itemQueryList.toArray())
        );

        const mouseOver$ = fromEventOutsideAngularZone<MouseEvent>(this.ngZone, this.elementRef.nativeElement, 'mouseover');

        combineLatest([
            itemsElements$,
            mouseOver$
        ]).pipe(
            tap(([itemsElements, event]) => {
                const itemIndex = itemsElements.findIndex(elementRef =>
                    elementRef.nativeElement === event.target
                );

                if (itemIndex >= 0) {
                    this.setPreselectedMenuItemIndex(itemIndex);
                    this.highlightItemAsPreselected(itemIndex);
                }
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    protected highlightItemAsPreselected(itemIndex: number): void {
        this.itemQueryList.forEach((elementRef, currentItemIndex) => {
            if (currentItemIndex === itemIndex) {
                this.renderer.addClass(elementRef.nativeElement, ITEM_HOVER_CLASS);
            } else {
                this.renderer.removeClass(elementRef.nativeElement, ITEM_HOVER_CLASS);
            }
        });
    }

    private listenToListItems(): void {
        timer(0).pipe(
            tap(() => this.changeDetectionSafeItems$.next(this.itemQueryList)),
            takeUntil(this.destroy$)
        ).subscribe();
    }

}
